import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ClerkProvider } from "@clerk/clerk-react";
import { dark } from "@clerk/themes";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import App from "./App";
import ConversationPage from "./pages/ConversationPage";
import DashboardPage from "./pages/DashboardPage";
import PracticePlanPage from "./pages/PracticePlanPage";
import SettingsPage from "./pages/SettingsPage";
import KnowledgePage from "./pages/KnowledgePage";
import VocabularyPage from "./pages/VocabularyPage";
import ExercisesPage from "./pages/ExercisesPage";
import UpgradePage from "./pages/UpgradePage";
import AdminLayout from "./components/admin/AdminLayout";
import AdminFeedbackPage from "./pages/AdminFeedbackPage";
import AdminSessionCostsPage from "./pages/AdminSessionCostsPage";
import SignInPage from "./pages/SignInPage";
import SignUpPage from "./pages/SignUpPage";
import { captureCheckoutFlagFromUrl } from "./lib/checkoutFlag";
import { initAnalytics } from "./lib/analytics";
import { captureReferralFromUrl, initPromoteKit } from "./lib/referral";
import { installAppHeightSync } from "./lib/appHeight";
import { websiteUrl } from "./lib/websiteUrl";
import { useTheme } from "./theme";
import "./styles/global.css";
import "./styles/tailwind.css";

ChartJS.register(CategoryScale, LinearScale, RadialLinearScale, PointElement, LineElement, ArcElement, Title, Tooltip, Legend, Filler);

// The marketing site deep-links into sign-up with ?plan=&billing=; stash it before
// Clerk's post-signup redirect drops the query string (see lib/checkoutFlag).
captureCheckoutFlagFromUrl();

// PostHog + the referral stash, before any route renders. Same reason as above:
// Clerk's redirect drops the query string, so ?ref=/?via= has to be read on first
// load. initPromoteKit is inert until VITE_PROMOTEKIT_ID is set (see lib/referral).
initAnalytics();
captureReferralFromUrl();
initPromoteKit();

// iOS standalone launch reports a stale viewport height; keep --app-height honest.
installAppHeightSync();

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;

/** Keeps Clerk's own UI (UserProfile modal, sign-in/up pages) in sync with the
 *  app's dark/light theme toggle. */
function Root() {
  const [theme] = useTheme();

  if (!PUBLISHABLE_KEY) {
    return (
      <div style={{ padding: "3rem", fontFamily: "system-ui", lineHeight: 1.6 }}>
        <h1>Auth not configured</h1>
        <p>
          Set <code>VITE_CLERK_PUBLISHABLE_KEY</code> in <code>app/.env</code> to enable sign-in.
        </p>
      </div>
    );
  }

  return (
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY}
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      afterSignOutUrl={websiteUrl()}
      appearance={{ baseTheme: theme === "dark" ? dark : undefined }}
    >
      <BrowserRouter>
        <Routes>
          <Route path="/sign-in" element={<SignInPage />} />
          <Route path="/sign-up" element={<SignUpPage />} />
          <Route path="/" element={<App />}>
            <Route index element={<ConversationPage />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="calendar" element={<PracticePlanPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="knowledge" element={<KnowledgePage />} />
            <Route path="vocabulary" element={<VocabularyPage />} />
            <Route path="exercises" element={<ExercisesPage />} />
            <Route path="upgrade" element={<UpgradePage />} />
            <Route path="admin" element={<AdminLayout />}>
              <Route index element={<AdminSessionCostsPage />} />
              <Route path="feedback" element={<AdminFeedbackPage />} />
            </Route>
          </Route>
        </Routes>
      </BrowserRouter>
    </ClerkProvider>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Root />
  </StrictMode>
);
